'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface TopbarProps {
  onToggleSidebar: () => void;
  onMobileMenuOpen: () => void;
  sidebarCollapsed: boolean;
}

const NOTIFICATIONS = [
  { id: 'notif-001', type: 'critical', title: 'QHSE Expired', body: 'Safety Harness batch SH-2024-07 expired 2 days ago at Site Alpha.', time: '2h ago' },
  { id: 'notif-002', type: 'warning', title: 'Low Stock Alert', body: 'Hydraulic Oil ISO 46 — only 12L remaining at Warehouse B (reorder: 50L).', time: '4h ago' },
  { id: 'notif-003', type: 'info', title: 'GRN Awaiting Approval', body: 'GRN-2026-0847 from Tata Steel Ltd requires your approval.', time: '5h ago' },
  { id: 'notif-004', type: 'warning', title: 'BOM Warning', body: 'Project PROJ-2026-014 Steel Structure — cement consumption at 87%.', time: '6h ago' },
  { id: 'notif-005', type: 'info', title: 'GI Status Change', body: 'GI-2026-0312 approved by Priya Sharma and ready for physical issuance.', time: '1d ago' },
];

const NOTIF_COLORS: Record<string, string> = {
  critical: 'bg-red-500',
  warning: 'bg-amber-400',
  info: 'bg-blue-400',
};

export default function Topbar({ onToggleSidebar, onMobileMenuOpen }: TopbarProps) {
  const [notifOpen, setNotifOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const unreadCount = NOTIFICATIONS.filter((n) => n.type === 'critical' || n.type === 'warning').length;

  return (
    <header className="h-[60px] bg-card border-b border-border flex items-center px-4 lg:px-5 gap-3 shrink-0 z-30 relative">
      {/* Mobile menu */}
      <button
        onClick={onMobileMenuOpen}
        className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground"
        aria-label="Open menu"
      >
        <Icon name="Bars3Icon" size={20} />
      </button>

      {/* Sidebar toggle desktop */}
      <button
        onClick={onToggleSidebar}
        className="hidden lg:flex p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
        aria-label="Toggle sidebar"
      >
        <Icon name="Bars3Icon" size={18} />
      </button>

      {/* Tenant badge */}
      <div className="hidden sm:flex items-center gap-2 pl-1">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/5 border border-primary/10 rounded-lg">
          <div className="w-1.5 h-1.5 rounded-full bg-teal animate-pulse" />
          <span className="text-[12px] font-600 text-foreground">Larsen & Toubro — Construction</span>
          <span className="text-[10px] bg-teal/10 text-teal px-2 py-0.5 rounded-full font-600 border border-teal/15">
            Professional
          </span>
        </div>
      </div>

      <div className="flex-1" />

      {/* Search */}
      <button className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-muted/60 rounded-lg text-[13px] text-muted-foreground hover:bg-muted transition-colors border border-border/60 min-w-[200px]">
        <Icon name="MagnifyingGlassIcon" size={14} />
        <span>Search items, GRNs…</span>
        <kbd className="text-[10px] bg-card border border-border px-1.5 py-0.5 rounded font-mono ml-auto">⌘K</kbd>
      </button>

      {/* Notifications */}
      <div className="relative">
        <button
          onClick={() => { setNotifOpen((p) => !p); setProfileOpen(false); }}
          className="relative p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          aria-label="Notifications"
        >
          <Icon name="BellIcon" size={19} />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-[9px] font-700 rounded-full flex items-center justify-center leading-none">
              {unreadCount}
            </span>
          )}
        </button>

        {notifOpen && (
          <div className="absolute right-0 top-full mt-2 w-[380px] bg-card border border-border rounded-2xl shadow-dropdown z-50 fade-in overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <span className="font-600 text-sm text-foreground">Notifications</span>
                <span className="text-[10px] bg-red-50 text-red-600 border border-red-200 px-2 py-0.5 rounded-full font-600">{unreadCount} new</span>
              </div>
              <button className="text-xs text-secondary hover:underline font-500">Mark all read</button>
            </div>
            <div className="max-h-80 overflow-y-auto scrollbar-thin">
              {NOTIFICATIONS.map((n) => (
                <div key={n.id} className="flex gap-3 px-4 py-3 hover:bg-muted/40 border-b border-border/40 last:border-0 cursor-pointer transition-colors">
                  <div className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${NOTIF_COLORS[n.type]}`} />
                  <div className="min-w-0">
                    <p className="text-[12px] font-600 text-foreground">{n.title}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">{n.body}</p>
                    <p className="text-[10px] text-muted-foreground/50 mt-1">{n.time}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="px-4 py-2.5 border-t border-border bg-muted/20">
              <button className="text-xs text-secondary hover:underline w-full text-center font-500">View all notifications</button>
            </div>
          </div>
        )}
      </div>

      {/* Profile */}
      <div className="relative">
        <button
          onClick={() => { setProfileOpen((p) => !p); setNotifOpen(false); }}
          className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-muted transition-colors"
        >
          <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center text-white text-[11px] font-700">
            AM
          </div>
          <div className="hidden md:block text-left">
            <p className="text-[12px] font-600 text-foreground leading-none">Arjun Mehta</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">Store Manager</p>
          </div>
          <Icon name="ChevronDownIcon" size={13} className="text-muted-foreground hidden md:block" />
        </button>

        {profileOpen && (
          <div className="absolute right-0 top-full mt-2 w-52 bg-card border border-border rounded-2xl shadow-dropdown z-50 fade-in overflow-hidden">
            <div className="px-4 py-3 border-b border-border">
              <p className="text-sm font-600 text-foreground">Arjun Mehta</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">arjun.mehta@lt-construction.com</p>
            </div>
            {[
              { label: 'My Profile', icon: 'UserIcon' },
              { label: 'Alert Settings', icon: 'BellIcon' },
              { label: 'Settings', icon: 'Cog6ToothIcon' },
            ].map((item) => (
              <button key={`profile-${item.label}`} className="flex items-center gap-3 w-full px-4 py-2.5 text-[13px] hover:bg-muted transition-colors text-left text-foreground">
                <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={15} className="text-muted-foreground" />
                {item.label}
              </button>
            ))}
            <div className="border-t border-border">
              <Link href="/sign-up-login" className="flex items-center gap-3 w-full px-4 py-2.5 text-[13px] text-red-600 hover:bg-red-50 transition-colors">
                <Icon name="ArrowRightOnRectangleIcon" size={15} />
                Sign Out
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}